import { useState } from 'react'
import { redirect, useLoaderData, useNavigate } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '@/components/subscription/button'
import { TextLink } from '@/components/subscription/text-link'
import { Checkbox } from '@/components/subscription/checkbox'
import { formatDate } from '@/lib/shipment'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Subscription, SubscriptionSchedule } from '@/api-client/data-contracts'
import * as styles from './styles.css'

export const loader = async () => {
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  if (!subscription) {
    return redirect('/subscription/mypage')
  }

  if (subscription.relationships.inventories.data.length !== 1) {
    return redirect('/subscription/mypage')
  }

  const schedules = await fetchPlannedSchedules(subscription, deliverWeekday(subscription.attributes.next_deliver_at))
  const defaultSchedule = findDefaultSchedule(schedules.data.data, subscription.attributes.next_deliver_at)
  const preview = await fetchPreview(subscription, defaultSchedule?.relationships.inventory.data.id)

  return [subscriptions, schedules, preview] as const
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'lossless_bouquet' })
}

function fetchPlannedSchedules(subscription: Subscription, weekday: number) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPlan(subscription.id, {
    per_month: '4',
    weekday: weekday.toString(),
  })
}

function fetchPreview(subscription: Subscription, inventoryId?: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPreview(subscription.id, { inventory_id: inventoryId ?? '' })
}

function findDefaultSchedule(schedules: SubscriptionSchedule[], nextDeliverAt: number) {
  const sorted = [...schedules].sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])

  return sorted.find((schedule) => schedule.attributes.deliver_at[0] === nextDeliverAt) ?? sorted[0]
}

// 火・水曜日着は火曜発送（2）、それ以外は金曜発送（5）
function deliverWeekday(nextDeliverAt: number) {
  const day = new Date(nextDeliverAt * 1000).getDay()

  return day === 2 || day === 3 ? 2 : 5
}

export const SubscriptionMypageNext: React.FC = () => {
  const [
    {
      data: { data: subscriptions },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: preview },
    },
  ] = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const subscription = subscriptions[0]

  const sortedSchedules = [...schedules].sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])
  const defaultSchedule =
    sortedSchedules.find((schedule) => schedule.attributes.deliver_at[0] === subscription.attributes.next_deliver_at) ?? sortedSchedules[0]
  const [selectedId, setSelectedId] = useState(defaultSchedule?.id)
  const [futureSchedules, setFutureSchedules] = useState(preview)
  const weekdayLabel = defaultSchedule ? formatDate(defaultSchedule.attributes.deliver_at[0] * 1000, 'E曜日') : ''

  const handleSelect = async (schedule: SubscriptionSchedule) => {
    setSelectedId(schedule.id)

    const { data } = await fetchPreview(subscription, schedule.relationships.inventory.data.id)
    setFutureSchedules(data.data)
  }

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async () => {
    const selected = sortedSchedules.find((schedule) => schedule.id === selectedId)

    if (!selected) {
      return
    }

    try {
      await createClient(Subscriptions).putSubscriptionsSubscriptionIdInventories(subscription.id, {
        'inventory_ids[]': [selected.relationships.inventory.data.id],
      })
      await navigate('/subscription/mypage', { state: { message: '次のお届け日を変更しました' } })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>次のお届け日を変更</PageHeading>
      <Flex direction="column" align="center" gap="24px" width="100%">
        <div className={styles.card}>
          <Flex direction="column" align="start" gap="4px">
            <p className={styles.cardHeading}>次に受け取りたい日を選んでください</p>
            <Flex direction="column" align="start" gap="4px" width="100%">
              {sortedSchedules.map((schedule) => (
                <Checkbox key={schedule.id} checked={selectedId === schedule.id} onChange={() => handleSelect(schedule)}>
                  {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M月d日（E）')}
                </Checkbox>
              ))}
            </Flex>
          </Flex>
        </div>
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <Flex direction="column" align="start" gap="4px" width="100%">
            <h3 className={styles.cardHeading}>変更後のお届け予定</h3>
            <p className={styles.note}>現在と同じ間隔（毎月・{weekdayLabel}）でお届けします</p>
          </Flex>
          <Flex direction="column" align="start" width="100%">
            {futureSchedules.map((schedule) => (
              <span key={schedule.id} className={styles.scheduledDate}>
                {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M月d日（E）')}
              </span>
            ))}
          </Flex>
        </Flex>
        <Flex direction="column" align="center" gap="16px" width="100%">
          <Button fluid as="button" onClick={handleSubmit}>
            次のお届け日を変更
          </Button>
          <TextLink to="/subscription/mypage">マイページに戻る</TextLink>
        </Flex>
      </Flex>
    </Flex>
  )
}
